import { FileQuestion, Users } from 'lucide-react';

import type { UseQueryResult } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import type { CandidateListItem, CandidatePage } from '@/api/schema';
import { Checkbox } from '@/components/ui/Checkbox';
import { EmptyState, ErrorState, NoData, NoResults } from '@/components/ui/EmptyState';
import { SkeletonTable } from '@/components/ui/Skeleton';
import { Table, TableWrap, Th } from '@/components/ui/Table';
import type { DateFormat } from '@/lib/dates';

import type { CandidateFilters } from '../api';
import { tableMinWidth, type CandidateColumn } from '../columns';
import { isFiltered } from '../urlState';
import { CandidateRow } from './CandidateRow';

/**
 * The table, and the four states it can be in.
 *
 * The pager is deliberately not here: the page renders it as a sibling, so its top
 * border reads as the table's footer and it does not appear underneath an error or an
 * empty state.
 */
export function CandidateTable({
  query,
  filters,
  columns,
  dateFormat,
  onSort,
  onClear,
  onRetry,
  onFirstPage,
  onOpen,
  selection,
}: {
  query: UseQueryResult<CandidatePage>;
  filters: CandidateFilters;
  /** Already narrowed to the visible set, in column order. */
  columns: readonly CandidateColumn[];
  dateFormat: DateFormat;
  onSort: (key: string) => void;
  onClear: () => void;
  onRetry: () => void;
  /** Used only by the past-the-end state. */
  onFirstPage: () => void;
  /** Opens the record. */
  onOpen: (id: string) => void;
  selection?:
    | {
        ids: string[];
        disabled: boolean;
        onToggle: (id: string, checked: boolean) => void;
        onToggleAll: (checked: boolean) => void;
        onDelete: (candidate: CandidateListItem) => void;
      }
    | undefined;
}) {
  // First paint only. Filtering and paging keep the previous rows on screen, because
  // the query holds them as placeholder data, so this is not a flash between pages.
  if (query.isPending) {
    return (
      <SkeletonTable
        rows={8}
        columns={Math.min(columns.length, 8)}
        label="Loading candidates"
      />
    );
  }

  if (query.isError) {
    return (
      <ErrorState
        message={
          query.error instanceof ApiError
            ? query.error.message
            : 'The candidate list did not load.'
        }
        onRetry={onRetry}
      />
    );
  }

  // Past the two guards above, the query is settled and the data is there.
  const items = query.data.items;
  const total = query.data.total;

  // Three different empty states, because they need three different answers.
  //
  // Wrapped in a live region: the pager carries the "showing 1-25 of 43" announcement,
  // but it renders nothing at all when the total is zero. Without this, filtering
  // a list down to nothing is completely silent.
  if (items.length === 0) {
    return (
      <div role="status" aria-live="polite">
        {/*
          Rows exist, just not on this page (a stale link to page 7 of a list that
          has since shrunk). Neither of the other two messages is true here: "no
          candidates yet" is a lie when there are forty, and "nothing matches your
          filters" sends somebody off to loosen filters that are working perfectly.
        */}
        {total > 0 ? (
          <EmptyState
            icon={<FileQuestion size={21} />}
            title="That page is past the end of the list"
            body={`There ${total === 1 ? 'is 1 candidate' : `are ${total} candidates`} to show. Go back to the first page.`}
            action={{ label: 'Back to the first page', onClick: onFirstPage }}
          />
        ) : isFiltered(filters) ? (
          <NoResults onClear={onClear} label="candidates" />
        ) : (
          <NoData
            icon={<Users size={21} />}
            title="No candidates yet"
            body="Add the first candidate, or import a spreadsheet, and they will appear here."
          />
        )}
      </div>
    );
  }

  return (
    <TableWrap>
      <Table minWidth={tableMinWidth(columns.length)}>
        <thead>
          <tr>
            {selection ? (
              <Th label="Select candidates">
                <Checkbox
                  aria-label="Select all candidates on this page"
                  checked={items.every((candidate) => selection.ids.includes(candidate.id))}
                  indeterminate={
                    selection.ids.length > 0 && selection.ids.length < items.length
                  }
                  disabled={selection.disabled}
                  onChange={(event) => {
                    selection.onToggleAll(event.target.checked);
                  }}
                />
              </Th>
            ) : null}
            {columns.map((column) =>
              column.sortKey ? (
                <Th
                  key={column.key}
                  label={column.label}
                  sortKey={column.sortKey}
                  activeKey={filters.sort_by}
                  direction={filters.sort_dir}
                  onSort={onSort}
                />
              ) : (
                <Th key={column.key} label={column.label} />
              ),
            )}
            {selection ? <Th label="Actions" /> : null}
          </tr>
        </thead>
        <tbody>
          {items.map((candidate) => (
            <CandidateRow
              key={candidate.id}
              candidate={candidate}
              columns={columns}
              dateFormat={dateFormat}
              onOpen={onOpen}
              selection={
                selection
                  ? {
                      checked: selection.ids.includes(candidate.id),
                      disabled: selection.disabled,
                      onToggle: selection.onToggle,
                      onDelete: selection.onDelete,
                    }
                  : undefined
              }
            />
          ))}
        </tbody>
      </Table>
    </TableWrap>
  );
}
